import React, { Suspense, useState } from 'react';
import Card from '@/components/card';
import Title from '@/components/title';
import CustomerTable from './components/customer-table';
import CustomerAddModal from './components/customer-add-modal';
import { IDataModal } from '@/interfaces/dataModal';
import {
  ICustomer,
  ISearchCustomerReq,
  ISearchCustomerRes,
} from '@/interfaces/customer';
import {
  Button,
  Dropdown,
  Input,
  MenuProps,
  TablePaginationConfig,
} from 'antd';
import { PlusOutlined, FilterOutlined } from '@ant-design/icons';
import { searchCustomers } from '@/adapters/customer';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { AxiosResponse } from 'axios';
import { useQuery } from '@tanstack/react-query';
import BusinessUnitSelector from '@/components/selector/business-unit-selector';
import UserSelector from '@/components/selector/user-selector';
import { useAuth } from '@/contexts/AuthContext';

const _customerModal: IDataModal<ICustomer> = {
  open: false,
};

const { Search } = Input;

const _criteria: ISearchCustomerReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
};

const Customer = () => {
  const { userInfo } = useAuth();
  const [criteria, setCriteria] = useState<ISearchCustomerReq>({
    ..._criteria,
    businessUnitId: userInfo?.bu || 0,
  });
  const [dataModal, setDataModal] =
    useState<IDataModal<ICustomer>>(_customerModal);

  const {
    data: customer,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery<AxiosResponse<ISearchCustomerRes>, Error>({
    queryKey: ['search-customer', criteria],
    queryFn: async () => await searchCustomers(criteria),
  });

  const onActionCustomer = () => {
    refetch();
    setDataModal({
      ..._customerModal,
    });
  };

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ICustomer> | SorterResult<ICustomer>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy =
        (sorter.column?.dataIndex as keyof ICustomer) || 'id';
      newCriteria.orderType = sorter.order || 'descend';
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      pageStart: 1,
      pageSize: 10,
    });
  };

  const handleMenuClick: MenuProps['onClick'] = e => {
    switch (e.key) {
      case '1':
        setCriteria({
          ...criteria,
          isActive: undefined,
          isDelete: undefined,
          pageStart: 1,
        });
        break;
      case '2':
        setCriteria({
          ...criteria,
          isActive: true,
          isDelete: undefined,
          pageStart: 1,
        });
        break;
      case '3':
        setCriteria({
          ...criteria,
          isActive: false,
          isDelete: undefined,
          pageStart: 1,
        });
        break;
      case '4':
        setCriteria({
          ...criteria,
          isActive: undefined,
          isDelete: true,
          pageStart: 1,
        });
        break;
      default:
        break;
    }
  };

  const items: MenuProps['items'] = [
    {
      label: 'ทั้งหมด',
      key: '1',
    },
    {
      label: 'ใช้งาน',
      key: '2',
    },
    {
      label: 'ไม่ใข้งาน',
      key: '3',
    },
    {
      label: 'ถังขยะ',
      key: '4',
    },
  ];

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  return (
    <Card>
      <Title title="จัดการลูกค้า">
        <div className="flex flex-row gap-2">
          <Button
            type="primary"
            className="max-w-[100px]"
            onClick={() => {
              setDataModal({
                ...dataModal,
                open: true,
              });
            }}
          >
            <PlusOutlined /> เพิ่มใหม่
          </Button>
        </div>
      </Title>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col md:flex-row gap-3">
          <Search
            className="md:max-w-[400px] md:min-w-[400px]"
            placeholder="ค้นหา"
            allowClear
            size="large"
            enterButton="ค้นหา"
            onSearch={onSearch}
          />
          <BusinessUnitSelector
            isAll
            handleSelectData={id => {
              setCriteria({
                ...criteria,
                businessUnitId: id,
                pageStart: 1,
              });
            }}
            disabled={!!userInfo?.bu}
            value={criteria.businessUnitId || undefined}
          />
          <UserSelector
            businessUnitId={criteria.businessUnitId}
            customClassName="md:w-[250px]"
            handleSelectData={function (id: string): void {
              setCriteria({
                ...criteria,
                salesId: id,
                pageStart: 1,
              });
            }}
            isAll
            allowClear
          />
          <Dropdown.Button
            className="w-[500px]"
            size="large"
            menu={menuProps}
            placement="bottom"
            icon={<FilterOutlined />}
          >
            สถานะ
          </Dropdown.Button>
        </div>
        <CustomerTable
          criteria={criteria}
          customer={customer?.data || { data: [], totalRecords: 0 }}
          loading={isLoading || isRefetching}
          onEditData={item =>
            setDataModal({ ..._customerModal, id: item.id, item, open: true })
          }
          onDelete={onActionCustomer}
          onTableChange={onTableChange}
        />
      </div>
      <Suspense>
        <CustomerAddModal
          handleCancel={() => setDataModal({ ..._customerModal })}
          handleOk={() => onActionCustomer()}
          dataModal={dataModal}
        />
      </Suspense>
    </Card>
  );
};

export default Customer;
